import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseComponent } from '../base-component/base-component';
import { bs5Connect } from '../../common/bs5Utils/bs5.mixin';
import { booleanConverter } from '../../common/converters';
import type { Article, CtaType, DisplayFormat, HeadingLevel } from './article-cards.interface';
import { fetchArticles } from './article-cards.api';
import { buildPaginationItems } from './article-cards.pagination';
import { CardRendererFactory, type CardRenderer, type RendererConfig } from './renderers/article-cards.renderer';
import { ArticleCardsAnalytics } from './article-cards.analytic';
import { ifDefined } from 'lit-html/directives/if-defined.js';

/**
 * Article Cards web component.
 *
 * @element mhecomm-article-cards
 *
 * @attr {DisplayFormat} display-format   - 'cards' | 'manual' | 'featured' | 'textHoriz' | 'textVert'
 * @attr {number}        per-row          - Cards per row (desktop). Default: 1.
 * @attr {number}        num-rows         - Rows per page. Default: 1.
 * @attr {HeadingLevel}  heading-level    - Card title heading level (2–6). Default: '2'.
 * @attr {string}        image-size       - Bootstrap object-fit suffix (e.g. 'cover'). Default: ''.
 * @attr {string}        image-position   - Bootstrap object-position suffix. Default: ''.
 * @attr {boolean}       show-image       - Show card images (cards format only).
 * @attr {boolean}       show-description - Show article description.
 * @attr {boolean}       show-tags        - Show article tags.
 * @attr {CtaType}       cta-type         - 'button' | 'link' | 'none'. Default: 'none'.
 * @attr {string}        api-url          - AEM servlet URL override. Default: /bin/mhprojects/ecomm/articlelist.
 * @attr {string}        tags             - Comma-separated tag filter (list formats).
 * @attr {string}        article-paths    - Comma-separated JCR paths (featured/manual formats).
 * @attr {string}        subarea          - Analytics subarea label.
 * @attr {string}        pagination-aria-label - Prefix for pagination nav aria-label.
 *
 */
@customElement('mhecomm-article-cards')
export class ArticleCards extends bs5Connect(BaseComponent) {

  @property() displayFormat: DisplayFormat = 'cards';
  @property({ type: Number }) perRow = 1;
  @property({ type: Number }) numRows = 1;
  @property() headingLevel: HeadingLevel = '2';
  @property() imageSize = '';
  @property() imagePosition = '';
  @property({ converter: booleanConverter }) showImage = false;
  @property({ converter: booleanConverter }) showDescription = false;
  @property({ converter: booleanConverter }) showTags = false;
  @property() ctaType: CtaType = 'none';
  @property() apiUrl = '/bin/mhprojects/ecomm/articlelist';
  @property() tags = '';
  @property() articlePaths = '';
  @property({ type: Number, attribute: 'data-manual-articles-size' }) manualArticlesSize = 3;
  @property() subarea = '';
  @property() groupAriaLabel = '';

  @state() private _articles: Article[] = [];
  @state() private _currentPage = 1;
  @state() private _totalPages = 0;
  @state() private _isLoading = true;
  @state() private _hasError = false;
  @state() private _low = 0;
  @state() private _high = 0;
  @state() private _total = 0;

  /**
   * Renderer strategy for the current displayFormat.
   */
  private _renderer!: CardRenderer;

  /**
   * Promise that resolves (or rejects) once the first page load has settled.
   * Consumed externally via the public `whenReady()` method.
   */
  private _readyResolve!: () => void;
  private _readyPromise = new Promise<void>(resolve => { this._readyResolve = resolve; });

  private get _listId(): string {
    return this.id || 'article-cards';
  }

  /**
   * Returns a Promise that settles once this instance's first page load is
   * complete (resolved on success or error). Lets external hash-navigation
   * logic know when this instance has finished loading before scrolling.
   */
  public whenReady(): Promise<void> {
    return this._readyPromise;
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadPage(1);
  }

  protected willUpdate(changedProperties: Map<string | number | symbol, unknown>): void {
    super.willUpdate(changedProperties);
    const RENDERER_PROPS = new Set([
      'displayFormat', 'headingLevel', 'imageSize', 'imagePosition',
      'showImage', 'showDescription', 'showTags', 'ctaType',
    ]);
    if ([...changedProperties.keys()].some(k => RENDERER_PROPS.has(k as string))) {
      this._renderer = CardRendererFactory.create(this.displayFormat, this._buildConfig());
    }
  }

  protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(changedProperties);
    this.bsInitAll('[data-bs-toggle="tooltip"]', window.bootstrap?.Tooltip);
  }

  renderComp(): TemplateResult {
    if (this._hasError) {
      return html`
        <div class="articlecards">
          <p class="articleList-remove">Something went wrong loading this content.</p>
        </div>
      `;
    }

    const { perRow, numRows, _isLoading, _articles, _totalPages } = this;
    const isFeatured = this.displayFormat === 'featured';
    const rowColsMd = perRow === 1 ? 1 : 2;
    const gridClasses = `list-unstyled row row-cols-1 row-cols-md-${rowColsMd} row-cols-lg-${perRow} g-4 p-0`;

    const listContent = _isLoading
      ? Array.from(
          { length: this._renderer.getSkeletonCount(perRow, numRows) },
          () => this._renderer.renderSkeleton(),
        )
      : _articles.map((a, i) => this._renderer.renderCard(a, i, _articles.length));

    return html`
      <div class="articlecards">
        ${isFeatured
          ? html`<div class="${gridClasses}">${listContent}</div>`
          : html`<ul aria-label="${ifDefined(this.groupAriaLabel || undefined)}" class="${gridClasses}" role="list">${listContent}</ul>`}
        ${_totalPages > 1 && !_isLoading ? this._renderPagination() : nothing}
      </div>
    `;
  }

  private _buildConfig(): RendererConfig {
    return {
      listId: this._listId,
      headingLevel: this.headingLevel,
      imageSize: this.imageSize,
      imagePosition: this.imagePosition,
      showImage: this.showImage,
      showDescription: this.showDescription,
      showTags: this.showTags,
      ctaType: this.ctaType,
      manualArticlesSize: this.manualArticlesSize,
      onCardClick: this._onCardClick,
      onCtaClick: this._onCtaClick,
    };
  }

  private async _loadPage(page: number, focusFirst = false): Promise<void> {
    this._isLoading = true;
    this._hasError = false;

    try {
      const resp = await fetchArticles(
        this.apiUrl,
        this.displayFormat,
        this.tags,
        this.articlePaths,
        page,
        this.perRow * this.numRows,
      );
      this._articles = resp.articleList;
      this._currentPage = resp.currentPageNumber;
      this._totalPages = resp.totalPages;
      this._low = resp.low;
      this._high = resp.high;
      this._total = resp.total;
    } catch (err) {
      console.error('[mhecomm-article-cards]', err);
      this._hasError = true;
    } finally {
      this._isLoading = false;
    }

    // Resolve the ready() promise on first load so external hash-navigation
    // logic knows this instance has settled. Subsequent calls to _loadPage
    // (pagination) are no-ops on the promise since it's already resolved.
    this._readyResolve();

    if (focusFirst) {
      void this.updateComplete.then(() => {
        this.shadowRoot
          ?.querySelector<HTMLElement>('.linkedCard, .card-link, a[href]')
          ?.focus();
      });
    }
  }

  private _renderPagination(): TemplateResult {
    const { _currentPage: cur, _totalPages: total, _low, _high, _total } = this;
    const items = buildPaginationItems(cur, total);
    const buttonLabel = this.groupAriaLabel || 'Pagination';
    const navLabel = this.groupAriaLabel ? `${buttonLabel} Pagination` : buttonLabel;
    const phraseText =
      _low === _high
        ? `Showing item ${_high} of ${_total} results`
        : `Showing ${_low}-${_high} of ${_total} ${buttonLabel} results`;

    const renderPageItem = (page: number | '...') => {
      if (page === '...') {
        return html`
          <li class="page-item align-self-center disabled">
            <span class="visually-hidden">ellipsis</span>
            <span class="ellipsis" aria-hidden="true">&hellip;</span>
          </li>`;
      }
      const isActive = cur === page;
      return html`
        <li class="page-item ${isActive ? 'active' : ''}" aria-current=${isActive ? 'page' : nothing}>
          <button class="page-link text-dark border-0"
            @click=${(e: Event) => this._onPaginationClick(e, page)}>
            <span class="visually-hidden">Page </span>${page}
          </button>
        </li>`;
    };

    const isPrevDisabled = cur === 1;
    const isNextDisabled = cur === total;

    return html`
      <div class="row g-4">
        <nav aria-label="${navLabel}">
          <ul class="pagination justify-content-end" id="${this._listId}-pagination-list">
            <li class="page-item ${isPrevDisabled ? 'disabled' : ''}">
              <button class="page-link text-dark border-0"
                data-bs-toggle="tooltip" data-bs-placement="top"
                title="Previous Page of ${buttonLabel}"
                ?disabled="${isPrevDisabled}"
                aria-label="Previous Page of ${buttonLabel}"
                @click=${(e: Event) => this._onPaginationClick(e, cur - 1)}
              >${this.uxtIconReady ? html`<uxt-icon icon="chevron-left"></uxt-icon>` : nothing}</button>
            </li>
            ${items.map(renderPageItem)}
            <li class="page-item ${isNextDisabled ? 'disabled' : ''}">
              <button class="page-link text-dark border-0"
                data-bs-toggle="tooltip" data-bs-placement="top"
                title="Next Page of ${buttonLabel}"
                ?disabled="${isNextDisabled}"
                aria-label="Next Page of ${buttonLabel}"
                @click=${(e: Event) => this._onPaginationClick(e, cur + 1)}
              >${this.uxtIconReady ? html`<uxt-icon icon="chevron-right"></uxt-icon>` : nothing}</button>
            </li>
          </ul>
        </nav>
        <p id="${this._listId}-paginationPhrase" aria-live="assertive">${phraseText}</p>
      </div>
    `;
  }

  private readonly _onCardClick = (article: Article) => (_e: Event): void => {
    const url = `${article.path}.html`;
    ArticleCardsAnalytics.cardClick(this, this._listId, url, this.subarea, article.tags);
    window.location.href = url;
  };

  private readonly _onCtaClick = (article: Article) => (e: Event): void => {
    e.stopPropagation();
    const url = `${article.path}.html`;
    const ctaComp = this.ctaType === 'link' ? 'link' : 'button';
    ArticleCardsAnalytics.ctaClick(this, this._listId, url, this.subarea, article.tags, ctaComp);
  };

  private _onPaginationClick(e: Event, page: number): void {
    e.preventDefault();
    if (page < 1 || page > this._totalPages || page === this._currentPage) return;
    ArticleCardsAnalytics.paginationClick(this, this._listId, page, this.subarea);
    void this._loadPage(page, true);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-article-cards': ArticleCards;
  }
}
